/**
 * Timeline audio cues — synced to the global sim clock.
 *
 * Uses Web Audio API (AudioContext + AudioBufferSourceNode) to avoid iOS
 * HTMLAudio autoplay restrictions and to keep timing stable against the
 * simulation clock.
 */

import audioCuesData from "../data/audio-cues.json";
import { clock } from "../stores/clock";
import { getScenarioState } from "../stores/scenario";
import type { SimTime } from "../types";
import { simTimeToSeconds } from "../types";

interface AudioCue {
	id: string;
	time: SimTime;
	seconds: number;
	file: string;
	volume?: number;
	buffer: AudioBuffer | null;
	source: AudioBufferSourceNode | null;
	gainNode: GainNode | null;
	startedAtCtxTime: number;
	startedFromOffset: number;
	loadPromise: Promise<void> | null; // Track individual cue loading
	loadAttempted: boolean; // Prevent multiple load attempts
}

const cues: AudioCue[] = audioCuesData.timelineCues.map((def, i) => ({
	id: `tcue-${i}`,
	time: def.time,
	seconds: simTimeToSeconds(def.time),
	file: def.file,
	volume: def.volume,
	buffer: null,
	source: null,
	gainNode: null,
	startedAtCtxTime: 0,
	startedFromOffset: 0,
	loadPromise: null,
	loadAttempted: false,
}));

let triggeredCues = new Set<string>();
let warnedLockedCues = new Set<string>();
let audioUnlocked = false;
let audioContext: AudioContext | null = null;
let globalMuted = false;

function getAudioContext(): AudioContext | null {
	if (typeof window === "undefined") return null;
	if (!audioContext) {
		const ContextCtor = window.AudioContext || (window as any).webkitAudioContext;
		if (!ContextCtor) {
			console.warn("Web Audio API is not available in this browser");
			return null;
		}
		audioContext = new ContextCtor();
	}
	return audioContext;
}

/** Adjust shots.mp3 timing based on crimeTime from scenarioMeta */
function adjustShotsTiming() {
	const shotsCue = cues.find((c) => c.file.includes("shots.mp3"));
	const meta = getScenarioState().meta;
	if (shotsCue && meta.crimeTime) {
		const crimeSeconds = simTimeToSeconds(meta.crimeTime);
		shotsCue.seconds = crimeSeconds - 2;

		const shotsSeconds = shotsCue.seconds;
		const hour = Math.floor(shotsSeconds / 3600);
		const minute = Math.floor((shotsSeconds % 3600) / 60);
		const second = shotsSeconds % 60;
		shotsCue.time = { hour, minute, second };
	}
}

function stopCue(cue: AudioCue) {
	if (cue.source) {
		try {
			cue.source.stop();
		} catch {
			// Already stopped.
		}
		cue.source.disconnect();
		cue.source = null;
	}
	if (cue.gainNode) {
		cue.gainNode.disconnect();
		cue.gainNode = null;
	}
}

/** Set global mute state for timeline audio */
export function setGlobalMuteTimeline(muted: boolean) {
	globalMuted = muted;
	// Update volume for all currently playing cues
	cues.forEach(cue => {
		if (cue.gainNode) {
			cue.gainNode.gain.value = globalMuted ? 0 : (cue.volume ?? 1.0);
		}
	});
}

/** Get current global mute state */
export function isGlobalMutedTimeline(): boolean {
	return globalMuted;
}

function playCue(cue: AudioCue, offsetSeconds = 0) {
	const ctx = getAudioContext();
	if (!ctx || !cue.buffer) return;
	if (ctx.state !== "running") return;

	const safeOffset = Math.max(0, Math.min(offsetSeconds, Math.max(0, cue.buffer.duration - 0.01)));

	stopCue(cue);

	const source = ctx.createBufferSource();
	source.buffer = cue.buffer;

	const gainNode = ctx.createGain();
	gainNode.gain.value = globalMuted ? 0 : (cue.volume ?? 1.0);

	source.connect(gainNode);
	gainNode.connect(ctx.destination);

	cue.source = source;
	cue.gainNode = gainNode;
	cue.startedAtCtxTime = ctx.currentTime;
	cue.startedFromOffset = safeOffset;

	source.onended = () => {
		if (cue.source === source) {
			cue.source.disconnect();
			cue.source = null;
		}
		if (cue.gainNode === gainNode) {
			cue.gainNode.disconnect();
			cue.gainNode = null;
		}
	};

	source.start(0, safeOffset);
}

/** Unlock audio playback on mobile (must be called from user interaction). */
export async function unlockTimelineAudio() {
	const ctx = getAudioContext();
	if (!ctx) return;

	try {
		if (ctx.state !== "running") {
			await ctx.resume();
		}
		// Prime the context with a near-silent short buffer to satisfy iOS gesture gate.
		const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
		const source = ctx.createBufferSource();
		const gain = ctx.createGain();
		gain.gain.value = 0.00001;
		source.buffer = buffer;
		source.connect(gain);
		gain.connect(ctx.destination);
		source.start(0);
		audioUnlocked = true;
		warnedLockedCues.clear();
	} catch {
		// Ignore: context will stay locked until a valid interaction succeeds.
	}
}
async function loadCueBuffer(cue: AudioCue, ctx: AudioContext): Promise<void> {
	if (cue.buffer || cue.loadAttempted) return;
	cue.loadAttempted = true;
	
	try {
		const response = await fetch(cue.file);
		if (!response.ok) throw new Error(`HTTP ${response.status}`);
		const arr = await response.arrayBuffer();
		cue.buffer = await ctx.decodeAudioData(arr.slice(0));
	} catch (err) {
		console.warn(`Timeline audio failed to load: ${cue.file}`, err);
	}
}

/**
 * Start loading a cue if not already loading/loaded.
 * Non-blocking - returns immediately, loading happens in background.
 */
function ensureCueLoading(cue: AudioCue) {
	if (cue.loadPromise || cue.buffer) return;
	
	const ctx = getAudioContext();
	if (!ctx) return;
	
	cue.loadPromise = loadCueBuffer(cue, ctx);
}

/**
 * Check current sim time and trigger any cues that are due.
 * Called each animation frame.
 * Now with smart prefetching: loads upcoming cues in advance.
 */
export function checkTimelineCues(simSeconds: number) {
	if (!getScenarioState().meta.soundEffects) return;
	adjustShotsTiming();

	// Suppress timeline audio at high speeds (>=5x) to prevent audio clogging
	const suppressAudio = clock.speedMultiplier >= 5;

	const PREFETCH_SECONDS = 30; // Start loading cues 30 seconds before they're needed

	for (const cue of cues) {
		// Prefetch upcoming cues
		if (!cue.buffer && !cue.loadPromise && simSeconds >= cue.seconds - PREFETCH_SECONDS) {
			ensureCueLoading(cue);
		}

		// Trigger cues that are due (skip if suppressed at high speed)
		if (triggeredCues.has(cue.id)) continue;
		if (simSeconds >= cue.seconds) {
			// Mark as triggered to prevent queuing up skipped cues
			triggeredCues.add(cue.id);
			
			// Skip playback at high speeds
			if (suppressAudio) continue;
			
			if (!cue.buffer) {
				// Not loaded yet - start loading if not already
				if (!cue.loadPromise) {
					ensureCueLoading(cue);
				}
				// Wait for next frame to check if loaded
				continue;
			}
			
			if (!audioUnlocked) {
				if (!warnedLockedCues.has(cue.id)) {
					console.warn(`Timeline audio blocked until user interaction: ${cue.file}`);
					warnedLockedCues.add(cue.id);
				}
				continue;
			}
			
			playCue(cue, 0);
		}
	}
}

/** Mark all audio cues up to simSeconds as already triggered. */
export function markPastCuesAsTriggered(simSeconds: number) {
	for (const cue of cues) {
		if (simSeconds >= cue.seconds) {
			triggeredCues.add(cue.id);
		}
	}
}

/** Pause all currently playing timeline cues. */
export function pauseTimelineCues() {
	const ctx = getAudioContext();
	if (!ctx) return;

	for (const cue of cues) {
		if (!cue.source || !cue.buffer) continue;
		const elapsed = Math.max(0, ctx.currentTime - cue.startedAtCtxTime);
		cue.startedFromOffset = Math.min(cue.buffer.duration, cue.startedFromOffset + elapsed);
		stopCue(cue);
	}
}

/** Resume any timeline cues that were mid-playback. */
export function resumeTimelineCues() {
	for (const cue of cues) {
		if (!cue.buffer) continue;
		if (triggeredCues.has(cue.id) && cue.startedFromOffset > 0 && cue.startedFromOffset < cue.buffer.duration) {
			playCue(cue, cue.startedFromOffset);
		}
	}
}

/** Stop all timeline cues and reset trigger state. */
export function resetTimelineCues() {
	triggeredCues.clear();
	warnedLockedCues.clear();
	for (const cue of cues) {
		stopCue(cue);
		cue.startedAtCtxTime = 0;
		cue.startedFromOffset = 0;
	}
}
